import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import { getBackgroundCheckExpirationState } from '@/backend/features/background-check/utils/backgroundCheckExpirationUtils';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { BackgroundCheckModal } from '@/ui/features/background-check/components/background-check-modal';
import { useRequestBackgroundCheckDetails } from '@/ui/features/background-check/hooks/useRequestBackgroundCheckDetails';
import { useSelectedPerson } from '@/ui/hooks/useSelectedPerson';
import { useSelectedPersonBackgroundCheckData } from '@/ui/hooks/useSelectedPersonBackgroundCheckData';
import { getBackgroundCheckIcon, getBackgroundCheckStatusLabel } from '@/ui/utils';
import { Column, TextCell } from '@juvare/common-data-table-ui/DataTable';
import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { GlobalLoader } from '@juvare/common-web-ui/GlobalLoader';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { SystemStatus } from '@juvare/contact-management/backend/features/accounts/shared';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { getUserState } from '@juvare/platform-sdk-react/ui/stores';
import { Row } from '@juvare/platform-shared/features/storage';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

export interface UseBackgroundCheckPanelColumnsProps {
    reload: () => void;
    numberOfActiveBackgroundCheckTypes: number;
}

export function useBackgroundCheckPanelColumns({
    reload,
    numberOfActiveBackgroundCheckTypes,
}: UseBackgroundCheckPanelColumnsProps): Column<Row<BackgroundCheckColumns>>[] {
    const { t } = useTranslation('rms');
    const navigate = useNavigate();
    const { handlePersonIdSelect, handlePersonIdReset, selectedPersonId } = useSelectedPerson();
    const { isAllowedToRequestBackgroundCheck } = useRequestBackgroundCheckDetails();
    const { backgroundCheckData, isLoading } = useSelectedPersonBackgroundCheckData(selectedPersonId);
    const userState = useSelector(getUserState);

    const { backgroundCheckConfig } = useSelector(getSettings);

    const { backgroundCheckExpirationPeriod } = useSelector(getSettings);

    return React.useMemo(() => {
        const handleReviewProfile = (e: React.MouseEvent<HTMLButtonElement>, { accountId }: { accountId: string }) => {
            e.stopPropagation();

            const searchParams = new URLSearchParams({ tab: 'backgroundcheck' });
            navigate(`/accounts/${accountId}?${searchParams.toString()}`);
        };

        const showModal = (row: Row<BackgroundCheckColumns>) => {
            return (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                event.stopPropagation();

                return handlePersonIdSelect(row.columns.accountId, (person: ExtendedAccountData) => {
                    showCustomDialog({
                        content: (close, ariaTitleId, ariaContentId) =>
                            isLoading || !backgroundCheckData ? (
                                <GlobalLoader />
                            ) : (
                                <BackgroundCheckModal
                                    account={person}
                                    backgroundCheck={row.columns}
                                    accountId={row.columns.accountId}
                                    ariaContentId={ariaContentId}
                                    ariaTitleId={ariaTitleId}
                                    onCloseModal={() => {
                                        close();
                                        handlePersonIdReset();
                                    }}
                                    onReloadAccounts={reload}
                                />
                            ),
                    });
                });
            };
        };

        const backgroundCheckColumns: Column<Row<BackgroundCheckColumns>>[] = [
            {
                headerLabel: t('credentialing.credentialVerificationDetails.labels.name'),
                sortKey: 'accountDisplayName',
                cellFunction: (row) => <TextCell>{row.columns.accountDisplayName}</TextCell>,
                tableCellSx: {
                    display: {
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            },
            {
                headerLabel: t('accountsFilters.backgroundCheck.status'),
                sortKey: 'status',
                cellFunction: (row) => {
                    const { Icon, color } = getBackgroundCheckIcon(row.columns.status);
                    const status = getBackgroundCheckStatusLabel({ status: row.columns.status, t });
                    return (
                        <TextCell>
                            <Stack
                                sx={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Icon htmlColor={color} />
                                <Typography
                                    sx={{
                                        fontSize: 'inherit',
                                    }}
                                >
                                    {status}
                                </Typography>
                            </Stack>
                        </TextCell>
                    );
                },
                tableCellSx: {
                    display: {
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            },
            ...(numberOfActiveBackgroundCheckTypes > 1
                ? ([
                      {
                          headerLabel: t('accountsFilters.backgroundCheck.type'),
                          sortKey: 'type',
                          cellFunction: (row) => (
                              <TextCell>{backgroundCheckConfig.type[row.columns.type as BackgroundCheckType].label}</TextCell>
                          ),
                          tableCellSx: {
                              display: {
                                  xs: 'none',
                                  sm: 'table-cell',
                              },
                          },
                      },
                  ] satisfies Column<Row<BackgroundCheckColumns>>[])
                : []),
            {
                cellFunction: (row) => {
                    const { isExpired, isExpiring } = getBackgroundCheckExpirationState(
                        {
                            backgroundCheckExpiredPeriodDays: backgroundCheckExpirationPeriod.backgroundCheckExpiredPeriodDays,
                            backgroundCheckExpiringPeriodDays: backgroundCheckExpirationPeriod.backgroundCheckExpiringPeriodDays,
                        },
                        row.columns,
                    );

                    return (
                        <TextCell>
                            {(isExpired || isExpiring) && (
                                <WarningIcon color='warning' titleAccess={t('pages.backgroundCheck.expiredOrExpiringSoon')} />
                            )}
                        </TextCell>
                    );
                },
                tableCellSx: {
                    display: {
                        width: '24px',
                        paddingLeft: 0,
                        paddingRight: 0,
                        textAlign: 'right',
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            },
            {
                headerLabel: t('backgroundCheck.labels.lastUpdated'),
                sortKey: 'updatedAt',
                cellFunction: (row) => {
                    const primaryBackgroundUpdatedAt = row.columns.updatedAt;
                    return <TextCell>{primaryBackgroundUpdatedAt && formatDateTime(new Date(primaryBackgroundUpdatedAt))}</TextCell>;
                },
                tableCellSx: {
                    display: {
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            },
            {
                cellFunction: (row) => {
                    const isOwnAccount = userState.id === row.columns.accountId;

                    if (backgroundCheckConfig.type[row.columns.type as BackgroundCheckType].process === 'manual' && !isOwnAccount) {
                        return (
                            <Button startIcon={<PublishedWithChangesOutlinedIcon />} variant='text' onClick={showModal(row)}>
                                {t('backgroundCheck.labels.changeStatus')}
                            </Button>
                        );
                    }

                    const backgroundCheckStatus = row.columns.status;
                    switch (backgroundCheckStatus) {
                        case 'formIncomplete':
                        case 'didNotConsent': {
                            return (
                                <Button startIcon={<CampaignOutlinedIcon />} variant='text' onClick={showModal(row)}>
                                    {t('backgroundCheck.labels.contactVolunteer')}
                                </Button>
                            );
                        }
                        case 'readyToSubmit': {
                            if (
                                isAllowedToRequestBackgroundCheck({
                                    targetAccount: {
                                        orgMembership: row.columns.accountOrgMembership,
                                        systemStatus: row.columns.accountStatus as SystemStatus,
                                        id: row.columns.accountId,
                                    },
                                    isBackgroundCheckTypeEnabled:
                                        backgroundCheckConfig.type[row.columns.type as BackgroundCheckType]?.enabled,
                                })
                            ) {
                                return (
                                    <Button startIcon={<PolicyOutlinedIcon />} variant='text' onClick={showModal(row)}>
                                        {t('backgroundCheck.labels.submitCheck')}
                                    </Button>
                                );
                            } else {
                                return '—';
                            }
                        }
                        case 'completeDenied':
                        case 'missingFields':
                        case 'processingError':
                        case 'processingNeedsAttention':
                        case 'reviewAndTakeAction':
                        case 'invalidSsn':
                        case 'unknown':
                            return (
                                <Button
                                    startIcon={<PersonSearchOutlinedIcon />}
                                    onClick={(e) => handleReviewProfile(e, { accountId: row.columns.accountId })}
                                >
                                    {t('backgroundCheck.labels.reviewProfile')}
                                </Button>
                            );
                        default:
                            return '—';
                    }
                },
                tableCellSx: {
                    display: {
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            },
        ];

        return backgroundCheckColumns;
    }, [
        t,
        navigate,
        handlePersonIdSelect,
        reload,
        backgroundCheckData,
        isLoading,
        handlePersonIdReset,
        isAllowedToRequestBackgroundCheck,
        backgroundCheckExpirationPeriod.backgroundCheckExpiredPeriodDays,
        backgroundCheckExpirationPeriod.backgroundCheckExpiringPeriodDays,
    ]);
}
